import React from 'react';
import { StaticMapProviderProps, StaticMapMarker, StaticMapUrlParams } from './StaticMapInterface';
import { LatLng, UniversalMapEntry } from './MapInterface';

/**
 * MapboxStaticImage Component
 * 
 * Renders static map images using Mapbox Static Images API
 * https://docs.mapbox.com/api/maps/static-images/
 */
const MapboxStaticImage: React.FC<StaticMapProviderProps> = ({
  entries,
  center,
  zoom = 10,
  width,
  height,
  showMarkers = true,
  className,
  style,
  onError,
  onLoad,
  alt = 'Mapbox static map',
  apiKey,
  baseUrl,
  mapType = 'roadmap',
}) => {
  /**
   * Get custom pin URL for markers
   */
  const getCustomPinUrl = (): string => {
    const isRetina = window.devicePixelRatio > 1;
    const pinFile = isRetina ? 'map_pin64x64.png' : 'map_pin32x32.png';
    return `${baseUrl}/${pinFile}`;
  };

  /**
   * Calculate center point from entries if not provided
   */
  const calculateCenter = (entries: UniversalMapEntry[]): LatLng => {
    if (entries.length === 0) {
      return { lat: 39.8283, lng: -98.5795 }; // Center of US as fallback
    }

    const sum = entries.reduce(
      (acc, entry) => ({
        lat: acc.lat + entry.location.lat,
        lng: acc.lng + entry.location.lng,
      }),
      { lat: 0, lng: 0 }
    );

    return {
      lat: sum.lat / entries.length,
      lng: sum.lng / entries.length,
    };
  };

  /**
   * Convert entries to static map markers
   */
  const entriesToMarkers = (entries: UniversalMapEntry[]): StaticMapMarker[] => {
    const customPinUrl = getCustomPinUrl();
    
    return entries.map((entry) => ({
      position: { lat: entry.location.lat, lng: entry.location.lng },
      iconUrl: customPinUrl,
      label: entry.title || '',
    }));
  };

  /**
   * Convert Google Maps mapType to Mapbox style
   */
  const getMapboxStyle = (mapType: string): string => {
    const styleMap: { [key: string]: string } = {
      'roadmap': 'streets-v11',
      'satellite': 'satellite-v9',
      'hybrid': 'satellite-streets-v11',
      'terrain': 'outdoors-v11'
    };
    return styleMap[mapType] || 'streets-v11';
  };

  /**
   * Generate Mapbox static map URL
   */
  const generateMapboxUrl = (params: StaticMapUrlParams): string => {
    const { center, zoom, width, height, markers, apiKey, scale = 1 } = params;
    
    // Base URL for Mapbox Static Images API
    const baseMapboxUrl = 'https://api.mapbox.com/styles/v1';
    const username = 'mapbox';
    const styleId = getMapboxStyle(mapType);
    
    // Check if we're in development mode with localhost
    const isDevelopment = process.env.NODE_ENV === 'development' || 
                         window.location.hostname === 'localhost' ||
                         window.location.hostname === '127.0.0.1';
    
    // Build overlay string for markers
    let overlayString = '';
    if (markers.length > 0 && showMarkers) {
      const markerOverlays = markers.map((marker) => {
        // In development, use default pins since Mapbox can't access localhost
        if (isDevelopment || !marker.iconUrl) {
          const color = marker.color || 'ff0000';
          return `pin-s+${color}(${marker.position.lng},${marker.position.lat})`;
        } else {
          // Custom pin with URL (production only)
          const encodedIconUrl = encodeURIComponent(marker.iconUrl);
          return `url-${encodedIconUrl}(${marker.position.lng},${marker.position.lat})`;
        }
      });
      overlayString = markerOverlays.join(',');
    }
    
    // Build the complete URL
    const scaleParam = scale > 1 ? '@2x' : '';
    const overlayPart = overlayString ? `/${overlayString}` : '';
    
    const url = `${baseMapboxUrl}/${username}/${styleId}/static${overlayPart}/${center.lng},${center.lat},${zoom}/${width}x${height}${scaleParam}?access_token=${apiKey}`;
    
    return url;
  };

  /**
   * Handle image load error
   */
  const handleImageError = () => {
    const errorMessage = 'Failed to load Mapbox static map image';
    console.error(errorMessage);
    if (onError) {
      onError(new Error(errorMessage));
    }
  };

  /**
   * Handle image load success
   */
  const handleImageLoad = () => {
    if (onLoad) {
      onLoad();
    }
  };

  // Validate API key
  if (!apiKey) {
    const errorMessage = 'Mapbox access token is required';
    console.error(errorMessage);
    if (onError) {
      onError(new Error(errorMessage));
    }
    return null;
  }

  // Calculate parameters
  const mapCenter = center || calculateCenter(entries);
  const markers = showMarkers ? entriesToMarkers(entries) : [];
  const scale = window.devicePixelRatio > 1 ? 2 : 1;

  // Generate URL parameters
  const urlParams: StaticMapUrlParams = {
    center: mapCenter,
    zoom,
    width,
    height,
    markers,
    apiKey,
    scale,
  };

  // Generate the static map URL
  const mapUrl = generateMapboxUrl(urlParams);

  // Log URL for debugging (remove in production)
  if (process.env.NODE_ENV === 'development') {
    console.log('Mapbox Static Map URL:', mapUrl);
  }

  return (
    <img
      src={mapUrl}
      alt={alt}
      className={className}
      style={{
        display: 'block',
        ...style, // Let style override width/height
      }}
      onError={handleImageError}
      onLoad={handleImageLoad}
    />
  );
};

export default MapboxStaticImage;